<script setup lang="ts">
// #843: 未認証の訪問者が公開作品を回遊できる面。
//
// これが無かったせいで、kitao referral（流入の 3 割）で作品ページに着地した人は
// **その 1 本を遊んで帰るしかなかった**:
//   - `/arcanelab` は `authenticated` ミドルウェアで未認証は入れない
//   - トップのギャラリーは 6 件打ち止め（`landing/PlaySection.vue` の `mode="fixed"`）で、
//     そこから先へのリンクがサイト内に 1 本も無かった
//   - `/works/<slug>` は immersive で、出口は「戻る」と作者リンクだけ
//
// `/arcanelab` は認証済みユーザーのアプリ内導線（冒険者キャンプ）のまま。ここは
// **未認証の入口**であって置き換えではない。
import type { ListWorksResponse, WorksSortKey } from '~/composables/api/useWorkApi'
import type { WorkGalleryTab } from '~/composables/useWorkGallery'

definePageMeta({
  layout: 'landing',
})

const runtimeConfig = useRuntimeConfig()
const api = useApi()

/**
 * 1 ページ目は SSR に載せる（`/c/<slug>` と同じ形）。**クローラと OGP のために
 * `<a href="/works/<slug>">` を HTML に出すことがこのページの主目的の半分**なので、
 * クライアント fetch では意味が無い。
 *
 * ⚠ `useWorkGallery` の `initialItems` は **追記時の perPage と同じ件数**でなければ
 * ならない。ズレると「次は page 2」の指す範囲が SSR 済みの範囲と食い違って作品を
 * 静かに取りこぼす。
 *
 * 件数は `/arcanelab` と同じ 15 に揃えてある（`@e2e` の公開作品は 12 件なので、
 * これより大きくすると 2 ページ目が生まれず追記スクロールの E2E が空振りする）。
 */
const PER_PAGE = 15

const emptyPage: ListWorksResponse = { works: [], total: 0, page: 1, perPage: PER_PAGE }

// API が落ちていてもページ自体は出す（クライアント側の初回ロードが引き継ぐ）。
const { data: firstPage } = await useAsyncData(
  'public-works-first-page',
  () => $fetch<ListWorksResponse>(
    `${runtimeConfig.public.apiBase}/api/v1/public/works`,
    { query: { page: 1, perPage: PER_PAGE, sort: 'new' } },
  ),
  { default: () => emptyPage },
)

// 「お気に入り」はサインインが要る（#724）ので、未認証の面には出さない。
const tabs: WorkGalleryTab[] = [
  { key: 'new', label: '新着' },
  { key: 'featured', label: '注目' },
  { key: 'popular', label: '人気' },
]
const sort = ref<WorksSortKey>('new')

// #802: 「注目」はサーバ側でシャッフルされるので、**シードを固定しないとページを
// 跨いだときに同じ作品が 2 枚出る**（`/arcanelab` と同じ扱い）。マウントごとに 1 つ
// 引いて、そのセッションの間は同じ並びを保つ。
const featuredSeed = Math.floor(Math.random() * 2 ** 31)

const fetchWorks = async (page: number, perPage: number, key: WorksSortKey) => {
  const resp = await api.listPublicWorks(page, perPage, key, key === 'featured' ? featuredSeed : undefined)
  return { works: resp.works, total: resp.total }
}

const title = 'みんなの作品 | Code & Magic'
const description = '魔法研究会に集まった Pyxel 作品を、ブラウザですぐ遊べます。登録もインストールも要りません。'
const ogImage = `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`

useHead({
  link: [{ rel: 'canonical', href: `${runtimeConfig.public.serviceDomain}/works` }],
})

useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
  ogImage,
  twitterCard: 'summary_large_image',
})

// JSON-LD: SSR 済みの 1 ページ目を ItemList で並べる。作品ページ側の CreativeWork
// （`/works/<slug>`）への内部リンクを構造化データとしても示す。
useJsonLd(computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  'name': 'Code & Magic の公開作品',
  'numberOfItems': firstPage.value?.total ?? 0,
  'itemListElement': (firstPage.value?.works ?? []).map((work, i) => ({
    '@type': 'ListItem',
    'position': i + 1,
    'url': `${runtimeConfig.public.serviceDomain}${work.publicUrl}`,
    'name': work.title,
  })),
})))
</script>

<template>
  <!-- 渦巻き（`SIZEN17A_33x30a.webp`）は **外周のガターとしてだけ** 使う地紋。
       その上に文字やカードを直接載せず、必ず「紙」に載せる。
       意匠は `/c/<slug>`（紺リボン + 羊皮紙）を継承する —— `/works` は
       「作者 1 人ぶんの羊皮紙を全作者に広げたもの」で構造が同じだから。
       トップの木枠は流用しない: 木枠は `background-size: 100% 100%` で 4 隅を
       器に合わせる演出なので、**無限に伸びる一覧に敷くと縦に引き伸ばされて破綻する**。 -->
  <div class="works-index">
    <div class="centered-area">
      <section class="works-panel">
        <header class="works-panel__head">
          <h1 class="works-panel__title">
            みんなの作品
          </h1>
          <p class="works-panel__lead">
            魔法研究会に集まった Pyxel 作品です。登録なしで、そのままブラウザで遊べます。
          </p>
        </header>

        <div class="works-panel__body">
          <WorkGallery
            v-model:sort="sort"
            :fetcher="fetchWorks"
            :per-page="PER_PAGE"
            :tabs="tabs"
            :initial-items="firstPage?.works"
            :initial-total="firstPage?.total"
            end-text="ここまでが、いま公開されている全作品です。"
          >
            <template #loading>
              <div class="works-panel__status">
                楽しいゲームを読み込み中 ...
              </div>
            </template>

            <template #empty>
              <div class="works-panel__status">
                公開されている作品はまだありません。
              </div>
            </template>

            <template #default="{ works }">
              <!-- #805: key は添字ではなく作品 ID。タブ切替で中身が総取っ替えに
                   なったとき、インスタンスが使い回されて `WorkCard` のローカル状態
                   （いいね数）が前の作品のまま残るのを防ぐ。 -->
              <div class="works-panel__grid">
                <WorkCard
                  v-for="work in works"
                  :key="work.workId"
                  :work="work"
                  :interactive="false"
                  :title-href="work.publicUrl"
                  @play="navigateTo(work.publicUrl || '')"
                />
              </div>
            </template>
          </WorkGallery>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
@import '~/assets/css/landing-variables.css';

/* `landing` レイアウトの `Header`（v-app-bar）は `app` prop で fixed ＝ フローを
   占有しない。自前で押し出さないと見出しがヘッダーの裏に潜る（`/c/<slug>` と同じ手当て）。 */
.works-index {
  --hdr-h: 64px;

  width: 100%;
  max-width: 100%;
  padding: 0;
  font-family: "Noto Sans JP", sans-serif;
  color: var(--landing-color-text);
}

.centered-area {
  width: 100%;
  max-width: var(--landing-max-width);
  margin: auto;
  padding: var(--landing-gutter);
  padding-top: calc(var(--hdr-h) + 1.5rem);
  padding-bottom: 2.5rem;
}

/* 羊皮紙のパネル。`/c/<slug>` のカードと同じ地・枠。ただし **固定 4:3 + 内部スクロールは
   しない** —— 一覧は追記スクロールで縦に伸び続けるので、ページ自身がスクロールする
   通常フローに乗せる（`/c` のモバイル崩しと同じ考え方）。 */
.works-panel {
  margin: 0 0.5em;
  background: linear-gradient(135deg, #f5e8d6 0%, #ecdbc1 100%);
  border: 1px solid rgba(140, 100, 50, 0.35);
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow-light);
  overflow: hidden;
  color: #2a1f10;
}

/* 紺リボン。クリーム文字との組み合わせは `/c/<slug>` で AA 実績あり。
   地紋の上に紺文字を置くのが元の失敗だったので、**文字の前に不透明な地を挟む**。 */
.works-panel__head {
  background: linear-gradient(135deg, #2c3e5a 0%, #1f2c44 100%);
  border-bottom: 2px solid #c9a440;
  color: #f4e4bf;
  padding: 1rem 1.5rem;
  text-align: center;
}

.works-panel__title {
  font-family: "Hiragino Mincho ProN", "Yu Mincho", "Noto Serif JP", "Noto Serif", serif;
  font-size: clamp(1.5rem, 3dvw, 2rem);
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 0.35em;
}

/* `opacity` の重ね掛けはしない（紙の上でも読めなくなる）。紺地に対する
   クリームの落とし込みで階層を作る。 */
.works-panel__lead {
  margin: 0;
  font-size: clamp(0.85rem, 1.2dvw, 0.95rem);
  line-height: 1.7;
  color: rgba(244, 228, 191, 0.8);
}

.works-panel__body {
  padding: clamp(16px, 3dvw, 32px);
  padding-bottom: 40px;
}

.works-panel__status {
  padding: 32px 0;
  text-align: center;
  font-size: 0.95rem;
  color: rgba(60, 40, 20, 0.75);
}

/* #670 Phase 1 の共通値（minmax 280px / gap 12px）に揃える。WorkCard 自身が
   `--cu`（カード幅基準）でサイジングするので、ここは列幅だけ決めれば足りる。 */
.works-panel__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  align-items: start;
  gap: 12px;
  margin-top: 16px;
}

/* モバイルは `/c/<slug>` と同じく full-bleed に崩す（枠・角丸・影を落とす）。 */
@media (max-width: 600px) {
  .centered-area {
    padding-left: 0;
    padding-right: 0;
    padding-top: calc(var(--hdr-h) + 0.75rem);
    padding-bottom: 1.5rem;
  }

  .works-panel {
    margin: 0;
    border-left: none;
    border-right: none;
    border-radius: 0;
    box-shadow: none;
  }

  .works-panel__head {
    padding: 0.7rem 1rem;
  }

  .works-panel__body {
    padding: 12px;
    padding-bottom: 24px;
  }

  /* スマホ幅では minmax 280px が中途半端（1 列だが横余白が出る）になるので
     1 カラム固定にする（`/c/<slug>` と同じ扱い）。 */
  .works-panel__grid {
    grid-template-columns: 1fr;
  }
}
</style>
